import { getDefaultLocaleTexts } from "./defaults/defaultLocaleTexts";
import type { ChatbotParams } from "./schemas/dochatConfig";
import type { HandoverNested } from "./schemas/handover";
import { RatingType } from "./schemas/satisfactionForm";
import { FsmDefMetaTypes } from "./types/fsmDef";

export const parseJsonValue = (value: unknown) => {
  if (!value || typeof value === "number" || typeof value === "boolean") {
    return undefined;
  }
  return typeof value === "string" ? JSON.parse(value) : value;
};

/**
 * The do-chat locale of a language code. do-chat keys its `availableLocales` as `fr_FR`, where the
 * response variants spell the same language `fr-fr`: handed the latter, do-chat finds no locale
 * and falls back to the default one. Anything that is not a language/region pair (a bare `fr`, an
 * unknown value) is returned unchanged.
 */
export const toDoChatLocale = (language: string): string => {
  const match = /^([a-z]{2,3})[-_]([a-z]{2})$/i.exec(language.trim());
  return match ? `${match[1].toLowerCase()}_${match[2].toUpperCase()}` : language;
};

/**
 * Whether do-chat completes the locales of a config with the languages of its version's response
 * variants (DAS-2768): only a chatbotv2 config, whose locales cannot route to a bot of their own,
 * that already offers a locale to take the title from.
 */
export const completesLocalesWithVariants = (
  availableLocales: ChatbotParams["availableLocales"] | undefined,
  chatbotv2: boolean | null | undefined
) => !!chatbotv2 && Object.keys(availableLocales ?? {}).length > 0;

/**
 * The locales do-chat offers: those of its config, completed with the languages of the version's
 * response variants (`de-de` → `de_DE`). Temporary (DAS-2768): nothing adds a translated language
 * to the config, so do-chat would not offer it. An added locale takes the title and subtitle of the
 * default locale and the placeholder of its own language.
 */
export const addVariantLocales = (
  availableLocales: ChatbotParams["availableLocales"] | undefined,
  variantLanguages: Iterable<string>,
  chatbotv2: boolean | null | undefined
): ChatbotParams["availableLocales"] | undefined => {
  if (!availableLocales || !completesLocalesWithVariants(availableLocales, chatbotv2)) {
    return availableLocales;
  }

  const offered = new Set(Object.keys(availableLocales).map(toDoChatLocale));
  const localeConfigs = Object.values(availableLocales);
  const defaultConfig = localeConfigs.find((config) => config?.isDefault) ?? localeConfigs[0];
  const added = Object.fromEntries(
    [...new Set([...variantLanguages].map(toDoChatLocale))]
      // do-chat locales are 5 characters long: a bare `gsw` or `zh-hant` has no locale to add.
      .filter((locale) => /^[a-z]{2}_[A-Z]{2}$/.test(locale) && !offered.has(locale))
      .map((locale) => [
        locale,
        {
          title: defaultConfig?.title || getDefaultLocaleTexts(locale).title,
          placeholder: getDefaultLocaleTexts(locale).placeholder,
          // The do-chat page falls back to a generic description without one: the root
          // `subTitle` is dropped once a config has locales.
          ...(defaultConfig?.subTitle ? { subTitle: defaultConfig.subTitle } : {}),
        },
      ])
  );

  return Object.keys(added).length > 0 ? { ...availableLocales, ...added } : availableLocales;
};

/**
 * Strips the deprecated `version` of a URL rewrite config and the `order` of its rules: the
 * do-config PATCH schema rejects them, yet configs saved before their removal still hold them.
 * do-config compares rules through it too, so a config still carrying them is not taken for a rule
 * change (DAS-2707).
 */
export const sanitizeUrlRewriteConfigForApi = (
  urlRewriteConfig: ChatbotParams["urlRewriteConfig"]
): ChatbotParams["urlRewriteConfig"] => {
  if (!urlRewriteConfig || typeof urlRewriteConfig !== "object") {
    return urlRewriteConfig;
  }

  const { version: _version, ...configWithoutVersion } =
    urlRewriteConfig as ChatbotParams["urlRewriteConfig"] & {
      version?: unknown;
    };

  if (!Array.isArray(configWithoutVersion.rules)) {
    return configWithoutVersion;
  }

  return {
    ...configWithoutVersion,
    rules: configWithoutVersion.rules.map((rule) => {
      if (!rule || typeof rule !== "object") {
        return rule;
      }

      const { order: _order, ...ruleWithoutOrder } = rule as typeof rule & { order?: unknown };
      return ruleWithoutOrder;
    }),
  };
};

/* eslint-disable no-template-curly-in-string */
export const handoverFsmDef = (handoverValues: HandoverNested) =>
  ({
    initial: "form",
    states: {
      form: {
        meta: {
          type: FsmDefMetaTypes.form,
          formSchema: {
            fields: {
              introduction: {
                type: "markdown",
                props: {
                  text: handoverValues.handover?.form.introduction,
                },
              },
              contact: {
                type: "textfield",
                props: {
                  label: handoverValues.handover?.form.emailLabel,
                  placeholder: "john.doe@dialonce.ai",
                  required: true,
                  type: "email",
                },
              },
              inquiry: {
                type: "textfield",
                props: {
                  label: handoverValues.handover?.form.inquiryPlaceholder,
                  minRows: 4,
                  multiline: true,
                  required: true,
                },
              },
            },
            submit: handoverValues.handover?.form.submit || "",
          },
        },
        on: {
          formOnSubmit: { target: "workflow" },
        },
      },
      workflow: {
        meta: {
          type: FsmDefMetaTypes.doBot,
          action: `workflow{"workflow_id":${handoverValues.handover?.workflow.id}}`,
          payloadTransformer: JSON.stringify({
            data: {
              formContact: "${form.contact}",
              inquiry: "${form.inquiry}",
              introduction: handoverValues.handover?.email.introduction,
              sendButton: handoverValues.handover?.email.sendButton,
              title: handoverValues.handover?.email.title,
              to: handoverValues.handover?.contacts.map(({ email }) => email).join(","),
            },
          }),
        },
      },
    },
  } as const);

// Highest rating still counted as a bad one, per rating type. Smileys and stars run 1..5,
// the number scale 0..10: 1..3 smileys and stars and 0..6 on the number scale are bad.
const BAD_SATISFACTION_RATING_MAX: Record<RatingType, number> = {
  [RatingType.Smiley]: 3,
  [RatingType.Stars]: 3,
  [RatingType.Numbers]: 6,
};

/**
 * Whether a satisfaction survey answer counts as a bad rating, i.e. one that sends the user
 * on to the standard "bad feedback" node when the survey asks for it (DAS-2868).
 *
 * The thresholds live here so the chat and anything else reading an answer agree on them.
 */
export const isBadSatisfactionRating = (ratingType: RatingType, ratingValue: number): boolean => {
  const max = BAD_SATISFACTION_RATING_MAX[ratingType];
  return max !== undefined && ratingValue <= max;
};
